"use client";

import { useState } from "react";
import { ResourceSidebar } from "../components/resource-sidebar";
import { SelectedSlot } from "@/types/slots";
import { ApiEndpoint, apiService } from "../../lib/api";
import { Page } from "../../types/page";
import { Booking } from "../../types/booking";
import { BookingForm } from "../components/booking-form";
import { toast } from "sonner";
import { EventSlots } from "./event-slots";
import { convertToMinutes } from "../../helper/utility";
import { BookingSuccess } from "../components/booking-success";
import { PageView } from "../../types/enum";
import { RescheduleForm } from "../components/reschedule-form";
import { EventDuration } from "../../types/event-duration";
import { usePixelTracker } from "../../hooks/pixel-tracker";

interface EventBookingProps {
  page: Page;
  resource: any;
  onDone: () => void;
  booking: Booking;
  layout?: "default" | "column";
  queryParams?: Record<string, string>;
  currentView?: PageView;
}

export function EventBooking({
  page,
  resource,
  onDone,
  booking: initialBooking,
  layout = "default",
  queryParams,
  currentView,
}: EventBookingProps) {
  const [currentStep, setCurrentStep] = useState<"slots" | "form" | "success">(
    "slots"
  );
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [booking, setBooking] = useState<Booking>(
    initialBooking ? initialBooking : ({} as Booking)
  );
  const tracker = usePixelTracker();

  const durations: EventDuration[] = resource.durations
    ? resource.durations
        .map((d: EventDuration) => ({ ...d, minutes: convertToMinutes(d) }))
        .sort((a: any, b: any) => a.minutes - b.minutes)
    : [{ type: "minutes", value: 30, is_default: false }];

  // If no default, set is_default on the first item
  if (!durations.some((d) => d.is_default) && durations.length > 0) {
    durations[0].is_default = true;
  }

  const handleFormSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const finalBooking: Booking = {
        ...booking,
        ...selectedSlot,
        ...data,
        resource_id: resource.resource_id,
        booking_url: `https://${page.domain}/${resource.slug}`,
      };
      const response = await apiService.post(
        ApiEndpoint.bookings,
        finalBooking
      );
      setBooking(response);
      setCurrentStep("success");

      // Meta, GA4 tracking
      tracker("booking_confirmed", {
        label: "Appointment Confirmed",
        booking_id: response.booking_id,
      });
    } catch (err: any) {
      toast.error(err.message || "Something went wrong, please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReschedule = async (reason: string) => {
    setIsSubmitting(true);
    const invitee = booking.invitees ? booking.invitees[0] : undefined;
    if (!invitee || !selectedSlot) return;

    try {
      const id = invitee.invitee_id + "_" + invitee.token;
      const data = {
        start_at: selectedSlot.start_at,
        reschedule_reason: reason,
      };
      const response = await apiService.put(
        ApiEndpoint.bookings,
        `${id}/reschedule`,
        data
      );
      setBooking(response);
      setCurrentStep("success");
    } catch (err: any) {
      toast.error(err.message || "Something went wrong, please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case "slots":
        onDone();
        break;
      case "form":
      case "success":
        setCurrentStep("slots");
        break;
      default:
        break;
    }
  };

  if (layout === "column") {
    return (
      <div className="h-[calc(100vh)]">
        <div className="grid grid-cols-[300px_1fr] h-full divide-x">
          <ResourceSidebar
            page={page}
            resource={resource}
            onBack={handleBack}
            booking={booking}
            currentView={currentView}
          />

          {currentStep === "slots" && (
            <EventSlots
              resource={resource}
              durations={durations}
              onSlotSelect={(slot) => {
                setSelectedSlot(slot);
                setCurrentStep("form");
              }}
              userId={
                currentView === "reschedule" ? booking.host?.user_id : undefined
              }
              layout="column"
            />
          )}

          {currentStep === "form" &&
            (currentView === "reschedule" ? (
              <RescheduleForm
                slot={selectedSlot}
                onSubmit={handleReschedule}
                onBack={handleBack}
                isSubmitting={isSubmitting}
              />
            ) : (
              <BookingForm
                page={page}
                resource={resource}
                queryParams={queryParams}
                onSubmit={handleFormSubmit}
                onBack={handleBack}
                slot={selectedSlot}
                isSubmitting={isSubmitting}
              />
            ))}

          {currentStep === "success" && (
            <BookingSuccess
              resource={resource}
              booking={booking}
              autoRedirect={currentStep === "success" ? true : false}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-[auto_1fr] xl:grid-cols-[300px_1fr] h-full divide-y lg:divide-y-0 lg:divide-x">
      <div className="lg:max-w-[300px] w-full">
        <ResourceSidebar
          page={page}
          resource={resource}
          onBack={handleBack}
          booking={booking}
          currentView={currentView}
        />
      </div>
      {currentStep === "slots" && (
        <EventSlots
          resource={resource}
          durations={durations}
          onSlotSelect={(slot) => {
            setSelectedSlot(slot);
            setCurrentStep("form");
          }}
          userId={
            currentView === "reschedule" ? booking.host?.user_id : undefined
          }
          layout="default"
        />
      )}

      {currentStep === "form" &&
        (currentView === "reschedule" ? (
          <RescheduleForm
            slot={selectedSlot}
            onSubmit={handleReschedule}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        ) : (
          <BookingForm
            page={page}
            resource={resource}
            queryParams={queryParams}
            onSubmit={handleFormSubmit}
            onBack={handleBack}
            slot={selectedSlot}
            isSubmitting={isSubmitting}
          />
        ))}

      {currentStep === "success" && (
        <BookingSuccess
          resource={resource}
          booking={booking}
          autoRedirect={currentStep === "success" ? true : false}
        />
      )}
    </div>
  );
}
